import { ROOM, SIDEWALK, INSTALLATION, CLICK_AND_COLLECT } from "components/funnel/constants";
import { PageEvent } from "types/tracking";
import {
  ProductImpression,
  RefurbishedProduct,
  RefurbishedProductFormattedForTracking,
  Category,
} from "types/refurbishedProduct";
import { FeasibleTimeslots, FormattedTimeslot, Timeslot } from "types/timeslot";
import { RefurbishedUserContext } from "types/user";
import { Cart, Payment, Shipping, DeliveryOptionType } from "types/cart";
import moment from "moment";

const EURO_CURRENCY = "EUR";

export function gtmPush(dataLayer: any) {
  window.dataLayer = window.dataLayer || [];
  // make sure to overwrite the non_interaction variable which might already be in the datalayer
  const dataLayerWithInteraction = { ...dataLayer, non_interaction: dataLayer.non_interaction || false };
  window.dataLayer.push(dataLayerWithInteraction);
  return dataLayer;
}

function getPageType(pageUrl: string) {
  //
  const url = pageUrl.substring(1).split("/");
  let pageType = "other";
  if ("reconditionne" == url[0]) {
    if (url.length == 1) {
      pageType = "homepage";
    }
    if (url.length >= 2) {
      pageType = "catalog";

      if ("panier" == url[1]) {
        pageType = "checkout";
        if (url[2] && url[2].includes("confirmation")) {
          pageType = "confirmation";
        }
      } else if ("mon-compte" == url[1]) {
        pageType = "account";
      } else {
        if (url.length == 4) {
          pageType = "product";
        }
      }
    }
  }

  if ("rendez-vous-reparateur" == url[0]) {
    pageType = "homerepair";
  }
  return pageType;
}

function getProductType(pageUrl: string) {
  const urlArray = pageUrl.substring(1).split("/");
  if (
    urlArray[0] === "reconditionne" &&
    [2, 3].includes(urlArray.length) &&
    !["panier", "mon-compte"].includes(urlArray[1])
  ) {
    // handle query param by taking only content before '?' character
    return urlArray[urlArray.length - 1].split("?")[0];
  }

  return null;
}

function getFormatedTimeslot(timeslot: FormattedTimeslot) {
  const tsDate = moment(timeslot.beginning);
  const tsDateString = tsDate.format("YYYY-MM-DD");
  const tsStartHour = tsDate.format("HH:mm");

  // output: { day: YYYY-MM-DD, hour: HH:mm }
  return { day: tsDateString, hour: tsStartHour };
}

function getFirstTimeslotInterval(timeslots: Timeslot[]) {
  if (timeslots.length === 0) {
    return "NA";
  }
  const timeslot = timeslots.sort((a, b) => (new Date(a.beginning) > new Date(b.beginning) ? 1 : -1))[0];

  if (!timeslot?.beginning) {
    return "NA";
  }
  const firstTimeslotDate = new Date(timeslot.beginning);
  firstTimeslotDate.setHours(8, 0, 0, 0);

  const todayDate = new Date();
  todayDate.setHours(8, 0, 0, 0);

  const diff = Math.abs(todayDate.getTime() - firstTimeslotDate.getTime());

  return Math.floor(diff / (1000 * 3600 * 24));
}
export const GTM_ID = "GTM-PH783VV";

export const GTM_SCRIPT_INIT = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${GTM_ID}');`;

export const GTM_IFRAME_INIT = `<iframe src="https://www.googletagmanager.com/ns.html?id=${GTM_ID}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;

const getProductFirstCatalogSlug = (product: RefurbishedProduct): string => {
  return product.catalogs.slice(-1).pop()?.slug || "";
};

const formatGA4EcommerceProduct = (product: RefurbishedProduct) => {
  return {
    item_id: String(product.id),
    location_id: getProductFirstCatalogSlug(product),
    item_name: `${product.product_type} ${product.brand} ${product.model_reference}`,
    item_brand: product.brand_slug,
    item_category: product.product_type_slug,
    item_variant: product.appearance_slug,
    price: product.price / 100,
    quantity: 1,
  };
};

const formatGA4EcommercePayload = (products: RefurbishedProduct[], amount: number, eventName: string) => {
  return {
    event: "GA4event",
    event_name: eventName,
    ecommerce: {
      currency: EURO_CURRENCY,
      value: amount / 100,
      items: products.map((product) => formatGA4EcommerceProduct(product)),
    },
  };
};

export const pageView = (url: string, user: RefurbishedUserContext | null = null) => {
  const pageType = getPageType(url);
  const pageEvent: PageEvent = {
    event: "pageview",
    url: url,
    login_status: "unlogged",
    page_type: pageType,
  };

  if (user && user.isAuthenticated) {
    const client = user.user;
    pageEvent.login_status = "logged";
    pageEvent.user_id = client.id ? client.id : undefined;
    pageEvent.hashed_email = client.email_hash;
  }
  setTimeout(() => {
    gtmPush(pageEvent);
  }, 1000);
};

export const pushCatalog = (category: Category, products: RefurbishedProduct[]) => {
  const impressions: ProductImpression[] = [];
  const newDataLayer = {
    event: "GAevent",
    category: undefined,
    ecommerce: {
      currencyCode: EURO_CURRENCY,
      impressions: impressions,
    },
  };

  for (const product of products) {
    newDataLayer.ecommerce.impressions.push({
      name: product.slug,
      brand: product.brand_slug,
      category: product.product_type_slug,
      list: category.slug,
      position: products.indexOf(product) + 1,
      dimension1: (product.best_price / 100).toFixed(2),
    });
  }

  gtmPush({ ecommerce: null }); // Clear the previous ecommerce object.
  return gtmPush(newDataLayer);
};

export const pushLocalOfferFilter = (zipcode: string, filterState: boolean) => {
  const newDataLayer = {
    event: "GAevent",
    zipcode: zipcode,
    local_offer: filterState,
  };
  return gtmPush(newDataLayer);
};

export const pushLocalOfferActivation = (zipcode: string, form: string) => {
  const newDataLayer = {
    event: "GA4event",
    event_name: "refurbished_activate_local_offer",
    zipcode: zipcode,
    local_offer_form: form,
  };
  return gtmPush(newDataLayer);
};

export const pushCatalogClick = (product: RefurbishedProduct, category: Category, position: string) => {
  const newDataLayer = {
    event: "GAevent",
    category: undefined,
    ecommerce: {
      click: {
        actionField: {
          list: category.slug,
        },
        products: [
          {
            name: product.slug,
            id: product.id,
            barcode: product.barcode,
            brand: product.brand_slug,
            category: product.product_type_slug,
            position: position,
            dimension1: (product.best_price / 100).toFixed(2),
          },
        ],
      },
    },
  };

  gtmPush({ ecommerce: null }); // Clear the previous ecommerce object.
  return gtmPush(newDataLayer);
};

export const pushProduct = ({ product }): { product: RefurbishedProduct } => {
  const newDataLayer = {
    event: "GAevent",
    category: undefined,
    ecommerce: {
      detail: {
        actionField: {
          list: product.product_type_slug,
        },
        products: [
          {
            name: product.slug,
            id: product.id,
            barcode: product.barcode,
            price: (product.price / 100).toFixed(2),
            brand: product.brand_slug,
            category: product.product_type_slug,
            variant: product.appearance_slug,
            dimension1: (product.price / 100).toFixed(2),
          },
        ],
      },
    },
  };

  gtmPush({ ecommerce: null }); // Clear the previous ecommerce object.
  gtmPush(newDataLayer);

  // GA4 migration - above code should be removed when UA tracking is disabled for GA4 tracking
  const GA4DatalayerPush = formatGA4EcommercePayload([product], product.price, "view_item");
  return gtmPush(GA4DatalayerPush);
};

export const pushAddProductToCart = (product: RefurbishedProduct) => {
  const newDataLayer = {
    event: "GAevent",
    category: "add_to_cart",
    ecommerce: {
      currencyCode: EURO_CURRENCY,
      add: {
        actionField: {
          list: product.product_type_slug,
        },
        products: [
          {
            name: product.slug,
            id: product.id,
            barcode: product.barcode,
            price: (product.price / 100).toFixed(2),
            brand: product.brand_slug,
            category: product.product_type_slug,
            variant: product.appearance_slug,
            dimension1: (product.price / 100).toFixed(2),
            quantity: 1,
          },
        ],
      },
    },
  };

  gtmPush({ ecommerce: null }); // Clear the previous ecommerce object.
  gtmPush(newDataLayer);

  // GA4 migration - above code should be removed when UA tracking is disabled for GA4 tracking
  const GA4DatalayerPush = formatGA4EcommercePayload([product], product.price, "add_to_cart");
  return gtmPush(GA4DatalayerPush);
};

const getShippingMethodSlug = (shippingMethod: DeliveryOptionType) => {
  switch (shippingMethod) {
    case ROOM:
      return "piece_au_choix";
    case INSTALLATION:
      return "installation";
    case SIDEWALK:
      return "pied_domicile";
    case CLICK_AND_COLLECT:
      return "click_and_collect";
    default:
      return "";
  }
};

export const pushShippingMethod = (
  step: string,
  shipping: Shipping,
  cartTotal: number,
  products: RefurbishedProduct[]
) => {
  const shippingMethod = getShippingMethodSlug(shipping.delivery_option_type);
  const newDataLayer = {
    event: "GAevent",
    category: "shipping_method_reconditionne",
    action: shippingMethod,
    label: (shipping.date + "_" + shipping.slot).replace(/\s/g, ""),
    ecommerce: {
      checkout_option: {
        actionField: {
          step: step,
          option: shippingMethod,
        },
      },
    },
  };

  gtmPush({ ecommerce: null }); // Clear the previous ecommerce object.
  gtmPush(newDataLayer);

  const GA4DatalayerPush = {
    event: "GA4event",
    event_name: "add_shipping_info",
    ecommerce: {
      currency: EURO_CURRENCY,
      amount: cartTotal / 100,
      shipping_tier: shippingMethod,
      items: products.map((product) => formatGA4EcommerceProduct(product)),
    },
  };

  return gtmPush(GA4DatalayerPush);
};

export const pushPaymentInformationFilled = (products: RefurbishedProduct[], paymentType: string, amount: number) => {
  gtmPush({ ecommerce: null }); // Clear the previous ecommerce object.

  const GA4DatalayerPush = {
    event: "GA4event",
    event_name: "add_payment_info",
    ecommerce: {
      currency: EURO_CURRENCY,
      amount: amount / 100,
      payment_type: paymentType,
      items: products.map((product) => formatGA4EcommerceProduct(product)),
    },
  };

  return gtmPush(GA4DatalayerPush);
};

export const pushViewCart = (products: RefurbishedProduct[], amount: number) => {
  gtmPush({ ecommerce: null }); // Clear the previous ecommerce object.

  const GA4DatalayerPush = formatGA4EcommercePayload(products, amount, "view_cart");
  return gtmPush(GA4DatalayerPush);
};

export const pushViewItemList = (path: string, count: number) => {
  const productType = getProductType(path);
  const newDatalayer = {
    event: "GA4event",
    event_name: "view_item_list",
    item_list_id: productType,
    item_list_name: productType,
    items: [],
    item_count: count,
  };
  return gtmPush(newDatalayer);
};

export const pushBeginCheckout = (products: RefurbishedProduct[], amount: number) => {
  gtmPush({ ecommerce: null }); // Clear the previous ecommerce object.

  const GA4DatalayerPush = formatGA4EcommercePayload(products, amount, "begin_checkout");
  return gtmPush(GA4DatalayerPush);
};

export const pushCompleteTransaction = (order: Cart, orderReference: string, payment: Payment) => {
  let shippingMethod = "pied_domicile";
  if (order.shipping.delivery_option_type == ROOM) {
    shippingMethod = "piece_au_choix";
  }
  const shippingFees = order.shipping.price;

  let warrantyFees = 0;
  const products: RefurbishedProductFormattedForTracking[] = [];
  for (const product of order.selection) {
    warrantyFees += product.warranty_price;

    products.push({
      name: product.product.slug,
      id: product.product.id,
      barcode: product.product.barcode,
      price: (product.product.price / 100).toFixed(2),
      brand: product.product.brand_slug,
      category: product.product.product_type_slug,
      variant: product.product.appearance_slug,
      quantity: 1,
      dimension1: (product.product.price / 100).toFixed(2),
    });
  }

  const newDataLayer = {
    event: "GAevent",
    category: undefined,
    ecommerce: {
      currencyCode: EURO_CURRENCY,
      purchase: {
        actionField: {
          id: orderReference,
          revenue: (payment.amount / 100).toFixed(2),
          tax: ((payment.amount / 100) * 0.2).toFixed(2),
          shipping: (shippingFees / 100).toFixed(2),
          warranty_amount: (warrantyFees / 100).toFixed(2),
          shipping_method: shippingMethod,
          shipping_selected_slot: (order.shipping.date + "_" + order.shipping.slot).replace(/\s/g, ""),
          coupon: order.coupon.code,
          coupon_value: (order.coupon.discount_raw_amount / 100).toFixed(2),
        },
        products: products,
      },
    },
  };

  gtmPush({ ecommerce: null }); // Clear the previous ecommerce object.
  gtmPush(newDataLayer);

  // GA4 migration - above code should be removed when UA tracking is disabled for GA4 tracking
  const GA4DatalayerPush = {
    event: "GA4event",
    event_name: "purchase",
    ecommerce: {
      currency: EURO_CURRENCY,
      value: payment.amount / 100,
      transaction_id: orderReference,
      coupon: order.coupon.code,
      items: order.selection.map((item) => formatGA4EcommerceProduct(item.product)),
    },
  };

  return gtmPush(GA4DatalayerPush);
};

export const pushFunnelStep = (step: string, products: RefurbishedProduct[], zipcode: string) => {
  const formattedProducts: RefurbishedProductFormattedForTracking[] = [];
  const newDataLayer = {
    event: "GAevent",
    zipcode: zipcode,
    category: undefined,
    ecommerce: {
      checkout: {
        actionField: {
          step: step,
        },
        products: formattedProducts,
      },
    },
  };

  for (const product of products) {
    newDataLayer.ecommerce.checkout.products.push({
      name: product.slug,
      id: product.id,
      barcode: product.barcode,
      price: (product.price / 100).toFixed(2),
      brand: product.brand_slug,
      category: product.product_type_slug,
      variant: product.appearance_slug,
      quantity: 1,
      dimension1: (product.price / 100).toFixed(2),
    });
  }

  gtmPush({ ecommerce: null }); // Clear the previous ecommerce object.
  return gtmPush(newDataLayer);
};

export const pushCompleteRepairTransaction = (
  postalCode: string,
  categorySlug: string,
  amount: number,
  fileNumber: string
) => {
  const newDataLayer = {
    event: "GA4event",
    event_name: "homerepair_confirmation",
    zipcode: postalCode,
    file_number: fileNumber,
    product_category: categorySlug,
    amount: amount,
  };

  return gtmPush(newDataLayer);
};

export const pushCompleteCollectTransaction = (categorySlug: string) => {
  const newDataLayer = {
    event: "GAevent",
    category: "COLLECT_FUNNEL",
    action: "COLLECT_COMPLETION",
    label: categorySlug,
  };

  return gtmPush(newDataLayer);
};

export const pushCheckoutFunnelEnter = (pageUrl: string) => {
  const newDataLayer = {
    event: "GAevent",
    category: "order",
    action: getPageType(pageUrl),
  };

  return gtmPush(newDataLayer);
};

export const pushFirstTimeslotDetails = (postalCode: string, dataTimeslots: FeasibleTimeslots) => {
  const newDataLayer = {
    event: "GA4event",
    event_name: "homerepair_slot_options",
    zipcode: postalCode,
    days_delay: getFirstTimeslotInterval(
      dataTimeslots.timeslots.length ? dataTimeslots.timeslots : [dataTimeslots.next_timeslot]
    ),
    slots_count: dataTimeslots.timeslots.length,
  };

  return gtmPush(newDataLayer);
};

export const pushSelectedTimeslotDetails = (
  postalCode: string,
  timeslot: FormattedTimeslot,
  productName: string,
  amount: number
) => {
  const newDataLayer = {
    event: "GA4event",
    event_name: "homerepair_slot_selected",
    zipcode: postalCode,
    day_selected: getFormatedTimeslot(timeslot)["day"],
    slot_selected: getFormatedTimeslot(timeslot)["hour"],
    product_category: productName,
    amount: amount,
  };
  return gtmPush(newDataLayer);
};

export const pushBeginFunnel = (postalCode: string, productName: string) => {
  const newDataLayer = {
    event: "GA4event",
    event_name: "homerepair_begin_funnel",
    zipcode: postalCode,
    product_category: productName,
  };
  return gtmPush(newDataLayer);
};

export const pushVisitFunnel = () => {
  const newDataLayer = {
    event: "GA4event",
    event_name: "homerepair_visit_funnel",
  };
  return gtmPush(newDataLayer);
};

export const pushRemovedFromCart = (product: RefurbishedProduct, location: string) => {
  const newDataLayer = {
    event: "GAevent",
    category: "remove_from_cart",
    action: location,
    ecommerce: {
      currencyCode: EURO_CURRENCY,
      remove: {
        products: [
          {
            name: product.slug,
            id: product.id,
            barcode: product.barcode,
            price: (product.price / 100).toFixed(2),
            brand: product.brand_slug,
            category: product.product_type_slug,
            variant: product.appearance_slug,
            dimension1: (product.price / 100).toFixed(2),
            quantity: 1,
          },
        ],
      },
    },
  };

  gtmPush({ ecommerce: null }); // Clear the previous ecommerce object.
  gtmPush(newDataLayer);

  const GA4DatalayerPush = formatGA4EcommercePayload([product], product.price, "remove_from_cart");
  return gtmPush(GA4DatalayerPush);
};

export const pushFunnelEnd = (eventCategory: string, postalCode: string, productName: string) => {
  const newDataLayer = {
    event: "GAevent",
    category: eventCategory,
    action: postalCode,
    label: productName,
  };

  return gtmPush(newDataLayer);
};

export const pushRepairMachineDetails = (
  productName: string,
  reference?: string,
  model?: string,
  postalCode?: string
) => {
  const newDataLayer = {
    event: "GA4event",
    event_name: "homerepair_machine_details",
    has_reference: !!reference || !!model,
    product_category: productName,
    zipcode: postalCode,
  };

  return gtmPush(newDataLayer);
};

export const pushGAEvent = (eventCategory: string, eventLabel: string, eventAction: string, eventValue?: string) => {
  const newDataLayer = {
    event: "GAevent",
    category: eventCategory,
    action: eventAction,
    label: eventLabel,
    value: eventValue,
  };

  return gtmPush(newDataLayer);
};

export const pushHomeRepairPrequalification = (diagnostic: string, productType: string, postalCode: string) => {
  const newDataLayer = {
    event: "GA4event",
    event_name: "homerepair_prequal",
    symptom: diagnostic,
    zipcode: postalCode,
    product_category: productType,
  };

  return gtmPush(newDataLayer);
};

export const pushHomeRepairZoneNotServed = (postalCode: string, productType: string) => {
  const newDataLayer = {
    event: "GA4event",
    event_name: "homerepair_zone_not_served",
    zipcode: postalCode,
    product_category: productType,
  };

  return gtmPush(newDataLayer);
};

export const pushHomeRepairNoRepairman = (postalCode: string, productType: string) => {
  const newDataLayer = {
    event: "GA4event",
    event_name: "homerepair_no_technician",
    zipcode: postalCode,
    product_category: productType,
  };

  return gtmPush(newDataLayer);
};
export const pushHomeRepairPersonalInformation = (postalCode: string, productType: string, amount: number) => {
  const newDataLayer = {
    event: "GA4event",
    event_name: "homerepair_personal_information",
    zipcode: postalCode,
    product_category: productType,
    amount: amount,
  };

  return gtmPush(newDataLayer);
};
export const pushHomeRepairPaymentError = (postalCode: string, productType: string, amount: number) => {
  const newDataLayer = {
    event: "GA4event",
    event_name: "homerepair_payment_error",
    zipcode: postalCode,
    product_category: productType,
    amount: amount,
  };

  return gtmPush(newDataLayer);
};
export const pushHomeRepairInitialSlotUnavailable = (
  postalCode: string,
  dataTimeslots: FeasibleTimeslots,
  productType: string,
  amount: number
) => {
  const newDataLayer = {
    event: "GA4event",
    event_name: "homerepair_initial_slot_unavailable",
    zipcode: postalCode,
    days_delay: getFirstTimeslotInterval(
      dataTimeslots.timeslots.length ? dataTimeslots.timeslots : [dataTimeslots.next_timeslot]
    ),
    product_category: productType,
    amount: amount,
  };

  return gtmPush(newDataLayer);
};

export const pushCollectionSlotOptions = (zipcode: string, slotCount: number, dayDelay: number) => {
  const dataLayerMessage = {
    event: "GA4event",
    event_name: "collection_slots_display",
    zipcode: zipcode,
    slots_count: slotCount,
    days_delay: dayDelay,
  };
  return gtmPush(dataLayerMessage);
};

export const pushCollectionSlotSelected = (zipcode: string, slotSelected: string, daySelected: string) => {
  const dataLayerMessage = {
    event: "GA4event",
    event_name: "collection_slots_selection",
    zipcode: zipcode,
    selected_slot: slotSelected,
    selected_day: daySelected,
  };
  return gtmPush(dataLayerMessage);
};

export const pushCollectionZoneNotServed = (postalCode: string) => {
  const newDataLayer = {
    event: "GA4event",
    event_name: "collection_zone_not_served",
    zipcode: postalCode,
  };
  return gtmPush(newDataLayer);
};

export const pushHomeRepairManagement = (fileNumber: string, productType: string) => {
  const newDataLayer = {
    event: "GAevent",
    category: "homerepair_management",
    action: fileNumber,
    label: productType,
  };

  gtmPush(newDataLayer);

  const GA4DataLayer = {
    event: "GA4event",
    event_name: "homerepair_management",
    file_number: fileNumber,
    product_category: productType,
  };

  return gtmPush(GA4DataLayer);
};

export const pushHomeRepairSlotReSchedule = (postalCode: string, delay: number) => {
  const newDataLayer = {
    event: "GAevent",
    category: "homerepair_slot_reschedule",
    action: postalCode,
    label: delay,
  };

  gtmPush(newDataLayer);
  const GA4DataLayer = {
    event: "GA4event",
    event_name: "homerepair_slot_reschedule",
    zipcode: postalCode,
    days_delay: delay,
  };

  return gtmPush(GA4DataLayer);
};

export const pushHomeRepairCancellation = (postalCode: string, fee: number) => {
  const newDataLayer = {
    event: "GAevent",
    category: "homerepair_cancellation",
    action: postalCode,
    label: fee ? "charged" : "free",
  };

  gtmPush(newDataLayer);

  const GA4DataLayer = {
    event: "GA4event",
    event_name: "homerepair_cancellation",
    zipcode: postalCode,
    value: fee / 100,
    currency: EURO_CURRENCY,
  };

  return gtmPush(GA4DataLayer);
};

export const pushHomeRepairRescheduleAccepted = (fileNumber: string, delay: number) => {
  const newDataLayer = {
    event: "GAevent",
    category: "homerepair_reschedule_accepted",
    action: fileNumber,
    label: delay,
  };
  gtmPush(newDataLayer);

  const GA4DataLayer = {
    event: "GA4event",
    event_name: "homerepair_reschedule_accepted",
    file_number: fileNumber,
    days_delay: delay,
  };
  return gtmPush(GA4DataLayer);
};

export const pushHomeRepairRescheduleRefused = (fileNumber: string, delay: number) => {
  const newDataLayer = {
    event: "GAevent",
    category: "homerepair_reschedule_refused",
    action: fileNumber,
    label: delay,
  };
  gtmPush(newDataLayer);

  const GA4DataLayer = {
    event: "GA4event",
    event_name: "homerepair_reschedule_refused",
    file_number: fileNumber,
    days_delay: delay,
  };
  return gtmPush(GA4DataLayer);
};

export const pushHomeRepairTimeslotCounterProposal = (fileNumber: string, counter_proposal: string) => {
  const newDataLayer = {
    event: "GAevent",
    category: "homerepair_timeslot_counter_proposal",
    action: fileNumber,
    label: counter_proposal,
  };
  gtmPush(newDataLayer);
  const GA4DataLayer = {
    event: "GA4event",
    event_name: "homerepair_timeslot_counter_proposal",
    file_number: fileNumber,
    proposal: counter_proposal,
  };
  return gtmPush(GA4DataLayer);
};
